import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, CheckCircle, AlertCircle, X, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import QRCodeStyling from "qr-code-styling";

interface TwoFASetupProps {
  userId: string;
  onComplete: () => void;
  onClose?: () => void;
}

export function TwoFASetup({ userId, onComplete, onClose }: TwoFASetupProps) {
  const [step, setStep] = useState<"loading" | "setup" | "verify" | "backup">("loading");
  const [secret, setSecret] = useState("");
  const [qrUri, setQrUri] = useState("");
  const [token, setToken] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const qrCodeInstance = useRef<QRCodeStyling | null>(null);

  // Check if 2FA is already enabled on mount (cookie is sent automatically)
  useEffect(() => {
    const check2FAStatus = async () => {
      try {
        const response = await fetch(`/api/setup-2fa?action=status&userId=${userId}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // ✅ send HttpOnly cookie
        });
        
        const data = await response.json();
        
        if (data.success && data.has2FA) {
          toast({ title: "2FA Already Enabled", description: "Two-factor authentication is set.", variant: "destructive" });
          onComplete();
          return;
        }
        
        setStep("setup");
      } catch (error) {
        console.error("Check 2FA status error:", error);
        setStep("setup");
      }
    };
    
    check2FAStatus();
  }, [userId, onComplete]);

  const handleSetup = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`/api/setup-2fa?action=setup&userId=${userId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSecret(data.secret);
        setQrUri(data.uri);
        setStep("verify");
        toast({ title: "Success", description: "Scan the QR code with your authenticator app." });
      } else {
        if (data.alreadyEnabled) {
          toast({ title: "Info", description: "2FA is already enabled for your account" });
          onComplete();
        } else {
          setError(data.error || "Failed to setup 2FA");
          toast({ title: "Error", description: data.error, variant: "destructive" });
        }
      }
    } catch (error) {
      console.error("Setup error:", error);
      setError("Failed to setup 2FA. Please try again.");
      toast({ title: "Error", description: "Failed to setup 2FA", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Generate QR code when we have the URI
  useEffect(() => {
    if (qrUri && qrCodeRef.current && !qrCodeInstance.current && step === "verify") {
      qrCodeInstance.current = new QRCodeStyling({
        width: 200,
        height: 200,
        type: "canvas",
        data: qrUri,
        image: "",
        dotsOptions: { color: "#4F46E5", type: "rounded" },
        backgroundOptions: { color: "#ffffff" },
        cornersSquareOptions: { type: "extra-rounded" }
      });
      qrCodeInstance.current.append(qrCodeRef.current);
    }
  }, [qrUri, step]);

  const handleVerify = async () => {
    if (!token || token.length !== 6) {
      toast({ title: "Error", description: "Please enter a valid 6-digit code", variant: "destructive" });
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(`/api/setup-2fa`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          action: "verify",
          userId: userId,
          code: token,
          enable: true 
        })
      });
      
      const data = await response.json();
      
      if (data.verified === true || data.success === true) {
        setBackupCodes(data.backupCodes || []);
        setStep("backup");
        toast({ title: "Success", description: "2FA enabled successfully!" });
      } else {
        setError(data.error || "Invalid verification code");
        toast({ title: "Error", description: data.error, variant: "destructive" });
      }
    } catch (error) {
      console.error("Verify error:", error);
      setError("Failed to verify 2FA. Please try again.");
      toast({ title: "Error", description: "Failed to verify 2FA", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast({ title: "Copied!", description: "Backup codes copied to clipboard" });
  };

  const handleComplete = async () => {
    try {
      await fetch('/api/setup-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: "include",
        body: JSON.stringify({ action: "complete", userId: userId })
      });
    } catch (err) {
      console.error("Failed to mark 2FA as completed:", err);
    }
    
    onComplete();
    if (onClose) onClose();
  };

  if (step === "loading") {
    return (
      <div className="bg-black/90 backdrop-blur-md rounded-2xl p-6 max-w-md w-full mx-auto border border-gray-700 shadow-2xl">
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          <p className="mt-4 text-gray-400">Checking 2FA status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black/90 backdrop-blur-md rounded-2xl p-6 max-w-md w-full mx-auto border border-gray-700 shadow-2xl">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white">2FA Authentication</h3>
        {onClose && step !== "backup" && (
          <button onClick={handleComplete} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {step === "setup" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-300">
            Scan QR code with Google Authenticator (or any TOTP app).
          </p>
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}
          <Button onClick={handleSetup} disabled={loading} className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold">
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Setting up...
              </div>
            ) : (
              "Enable 2FA"
            )}
          </Button>
        </div>
      )}

      {step === "verify" && qrUri && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-gray-300 mb-2">Scan with Google Authenticator</p>
            <div className="inline-block p-4 bg-white rounded-lg mb-4">
              <div ref={qrCodeRef} className="w-48 h-48" />
            </div>
            <p className="text-xs text-gray-400 break-all">
              Secret: <span className="font-mono text-blue-400">{secret}</span>
            </p>
          </div>
          
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          )}
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">Enter 6-digit code</label>
            <Input
              type="text"
              maxLength={6}
              value={token}
              onChange={(e) => setToken(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="text-center text-2xl font-mono bg-gray-800 border-gray-700 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              autoFocus
            />
            <Button 
              onClick={handleVerify} 
              disabled={loading || token.length !== 6} 
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </div>
              ) : (
                "Verify & Enable"
              )}
            </Button>
          </div>
        </div>
      )}

      {step === "backup" && (
        <div className="space-y-4">
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-yellow-500">Save Backup Codes</p>
                <p className="text-xs text-gray-300 mt-1">
                  Store these codes safely. Each code can be used once.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm text-white">
              {backupCodes.map((code, i) => (
                <div key={i} className="text-center bg-gray-700/50 p-2 rounded">
                  {code}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleCopyCodes} variant="outline" className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-800 hover:text-white">
              <Copy className="w-4 h-4 mr-2" />
              {copied ? "Copied!" : "Copy Codes"}
            </Button>
            <Button onClick={handleComplete} className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold">
              <CheckCircle className="w-4 h-4 mr-2" />
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
